import { Controller } from "@hotwired/stimulus"

const LOADING_INDICATOR_DELAY_MS = 100
const GROUP_COUNT_SELECTORS = {
  primary: "#genre_filters .filter-chip__count, #sport_filters .filter-chip__count",
  channels: "#channel_filters .channel-option__count",
}

function toggleGroupLoading(element, group, loading) {
  const selector = GROUP_COUNT_SELECTORS[group]
  if (!selector) return

  element.querySelectorAll(selector).forEach((count) => {
    count.classList.toggle("is-loading", loading)
    if (loading) {
      count.setAttribute("aria-busy", "true")
    } else {
      count.removeAttribute("aria-busy")
    }
  })
}

// Loads predictive counts outside the result-search critical path. Mobile loads
// primary chips when search opens and defers channels until their screen opens.
// Desktop requests both groups after paint because both are immediately visible.
export default class extends Controller {
  static values = { url: String }

  connect() {
    this.requests = new Map()
    this.loadingTimers = new Map()
    this.loadedGroups = new Set()
    this.boundLoadPrimary = () => this.loadGroup("primary")
    this.boundLoadChannels = () => this.loadGroup("channels")
    this.element.addEventListener("facets:load", this.boundLoadPrimary)
    this.element.addEventListener("facets:load-channels", this.boundLoadChannels)
    this.boundResultsUpdated = this.resultsUpdated.bind(this)
    document.addEventListener("turbo:before-stream-render", this.boundResultsUpdated)

    if (this.desktop && !this.element.dataset.coldModal) {
      this.frame = window.requestAnimationFrame(() => this.loadVisibleGroups())
    }
  }

  disconnect() {
    this.element.removeEventListener("facets:load", this.boundLoadPrimary)
    this.element.removeEventListener("facets:load-channels", this.boundLoadChannels)
    document.removeEventListener("turbo:before-stream-render", this.boundResultsUpdated)
    window.cancelAnimationFrame(this.frame)
    clearTimeout(this.resultsTimer)
    clearTimeout(this.refreshTimer)
    this.loadingTimers.forEach((timer) => clearTimeout(timer))
    this.requests.forEach((request) => request.abort())
  }

  async loadGroup(group) {
    const form = this.element.querySelector("form")
    if (!form || !this.hasUrlValue) return

    this.requests.get(group)?.abort()
    const request = new AbortController()
    this.requests.set(group, request)
    this.loadingTimers ||= new Map()
    clearTimeout(this.loadingTimers.get(group))
    this.loadingTimers.set(group, setTimeout(() => {
      if (request === this.requests.get(group)) {
        toggleGroupLoading(this.element, group, true)
      }
    }, LOADING_INDICATOR_DELAY_MS))
    const params = new URLSearchParams(new FormData(form))
    params.delete("embedded")
    params.delete("page")
    params.set("facet_group", group)

    try {
      const response = await fetch(`${this.urlValue}?${params.toString()}`, {
        signal: request.signal,
        credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html" },
      })
      if (!response.ok || response.redirected || request !== this.requests.get(group)) return

      const stream = await response.text()
      if (request !== this.requests.get(group)) return
      window.Turbo?.renderStreamMessage(stream)
      this.loadedGroups.add(group)
    } catch (error) {
      if (error.name !== "AbortError") this.loadedGroups.delete(group)
    } finally {
      if (request === this.requests.get(group)) {
        clearTimeout(this.loadingTimers.get(group))
        this.loadingTimers.delete(group)
        toggleGroupLoading(this.element, group, false)
        this.requests.delete(group)
      }
    }
  }

  loadVisibleGroups() {
    this.loadGroup("primary")
    if (this.desktop || this.loadedGroups.has("channels")) this.loadGroup("channels")
  }

  refresh() {
    const open = this.element.modal?.isOpen
    if (!(this.loadedGroups.has("primary") || this.desktop || open)) return

    clearTimeout(this.refreshTimer)
    this.refreshTimer = setTimeout(() => this.loadVisibleGroups(), 100)
  }

  // Results can also change through active-filter and Clear links rather than a
  // form submit. Wait until all sibling Turbo streams have synchronized the
  // form controls, then predict from that committed state.
  resultsUpdated(event) {
    const stream = event.target
    if (stream?.getAttribute?.("target") !== "search_results") return

    clearTimeout(this.resultsTimer)
    this.resultsTimer = setTimeout(() => this.refresh(), 0)
  }

  get desktop() {
    return window.matchMedia("(min-width: 768px)").matches
  }
}
